import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { Check } from 'lucide-react';
import { cn } from '../lib/cn.js';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: ReactNode;
  description?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, description, className, id, disabled, ...rest },
  ref,
) {
  return (
    <label
      htmlFor={id}
      className={cn(
        'group inline-flex items-start gap-3 cursor-pointer min-h-[44px] py-1',
        disabled && 'cursor-not-allowed opacity-60',
        className,
      )}
    >
      <span className="relative inline-flex pt-0.5">
        <input
          ref={ref}
          id={id}
          type="checkbox"
          disabled={disabled}
          className="peer sr-only"
          {...rest}
        />
        <span
          aria-hidden
          className={cn(
            'inline-flex size-5 items-center justify-center rounded border border-border-strong bg-bg-surface-2 transition-colors',
            'peer-checked:bg-accent-base peer-checked:border-accent-base',
            'peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-accent-base',
          )}
        >
          <Check className="size-3.5 text-text-inverse opacity-0 peer-checked:opacity-100 group-has-[input:checked]:opacity-100" strokeWidth={3} />
        </span>
      </span>
      {(label || description) && (
        <span className="flex flex-col">
          {label && <span className="text-base text-text-primary leading-tight">{label}</span>}
          {description && <span className="text-sm text-text-secondary mt-0.5">{description}</span>}
        </span>
      )}
    </label>
  );
});
